import React, {
    createContext,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";
import type { GQL } from "@binale-tech/shared";
import { useApolloClient } from "@apollo/client/react";
import { UserContext } from "@domain/context/UserProvider";
import { toast } from "@heroui/react";
import { datevUserInfoQuery } from "@domain/context/queries/ctxQueries.graphql";
import { captureException } from "@sentry/react";
import { datevTokenRefresh } from "@domain/infrastructure/oauth/oauth";

interface IDatevContext {
    datevUserInfo?: GQL.IDatevUserInfo;
    token?: GQL.IOAuthTokenMeta;
    isLoading: boolean;
}

const defaultValue: IDatevContext = {
    datevUserInfo: null,
    token: null,
    isLoading: false,
};
type IDatevControlContext = {
    triggerRefetch?: () => Promise<void | boolean>;
};
export const DatevContext = createContext<IDatevContext>(defaultValue);
export const DatevControlContext = createContext<IDatevControlContext>({});

export const DatevContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const client = useApolloClient();
    const { fireUser } = use(UserContext);
    const [isLoading, setIsLoading] = useState(false);
    const [token, setToken] = useState<GQL.IOAuthTokenMeta>();
    const [datevUserInfo, setDatevUserInfo] = useState<GQL.IDatevUserInfo>();

    const reset = useCallback(() => {
        setDatevUserInfo(null);
        setToken(null);
        setIsLoading(false);
    }, []);
    const refetch = useCallback(async () => {
        setIsLoading(true);
        const info = await client
            .query({
                query: datevUserInfoQuery,
                fetchPolicy: "no-cache",
            })
            .then((res) => {
                setDatevUserInfo(res?.data?.datevUserInfo);
                return res?.data?.datevUserInfo;
            });
        if (info) {
            await client
                .mutate({
                    mutation: datevTokenRefresh,
                    variables: { input: {} },
                })
                .then((res) => {
                    setToken(res.data.datevTokenRefresh);
                })
                .catch((e) => {
                    if (e.message.includes("Refresh token is missing or expired")) {
                        setToken(null);
                        return;
                    }
                    if (e.message.includes("outgoing request timed out")) {
                        return;
                    }
                    captureException(e);
                    toast.danger(e.message);
                });
        }
        setIsLoading(false);
    }, [client]);
    useEffect(() => {
        if (!fireUser?.uid) {
            setTimeout(() => reset(), 10);
        } else {
            setTimeout(() => refetch(), 10);
        }
    }, [client, fireUser?.uid, refetch, reset]);
    const value: IDatevContext = useMemo(
        () => ({
            isLoading,
            token,
            datevUserInfo,
        }),
        [isLoading, token, datevUserInfo],
    );
    const control: IDatevControlContext = useMemo(
        () =>
            ({
                triggerRefetch: () => refetch(),
            }) satisfies IDatevControlContext,
        [refetch],
    );
    return (
        <DatevContext value={value}>
            <DatevControlContext value={control}>{children}</DatevControlContext>
        </DatevContext>
    );
};
